import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { ValidationErrorService } from 'src/app/core/validation/validation-error.service';
import { PASSTHROUGH_STATUS_CODES, SKIP_ERROR_HANDLING } from 'src/app/shared/context/error-handling.context';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private messageService: MessageService,
    private validationErrorService: ValidationErrorService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        // Fehler überspringen
        if (req.context.get(SKIP_ERROR_HANDLING)) {
          return throwError(() => error);
        }

        // Bestimmte Status-Codes still durchreichen (kein Toast, catchError im Store übernimmt)
        if (req.context.get(PASSTHROUGH_STATUS_CODES).includes(error.status)) {
          return throwError(() => error);
        }

        if (error.status === 0) {
          // Netzwerkfehler (z.B. keine Verbindung oder CORS-Problem)
          this.messageService.add({severity: 'error', summary: 'Netzwerkfehler', detail: 'Keine Verbindung zum Server.'});
        } else {
          switch (error.status) {
            case 400:
              if (error.error?.errors) {
                // Validierungsfehler-Objekt von der API
                this.messageService.add({severity: 'warn', summary: 'Einige Eingaben sind fehlerhaft', detail: 'Bitte prüfen Sie die mit Fehlern gekennzeichneten Eingabefelder.'});
                this.validationErrorService.set(error.error.errors);
              } else {
                // Allgemeiner Bad Request ohne Validierungsobjekt
                this.messageService.add({severity: 'error', summary: 'Fehler', detail: this.detailOf(error, 'Ungültige Anfrage.')});
              }
              break;
              case 401:
                this.messageService.add({severity: 'error', summary: 'Nicht autorisiert', detail: 'Ihre Sitzung ist abgelaufen oder Sie sind nicht angemeldet. Bitte melden Sie sich erneut an.'});
                break;
            case 403:
              // Nicht autorisiert / Forbidden
              this.messageService.add({severity: 'error', summary: 'Zugriff verweigert', detail: 'Ihre Rolle erlaubt diese Aktion nicht.'});
              // Optional: Zentralen Logout/Redirect anstoßen
              //this.authService.logout?.();
              break;
            case 404:
              this.messageService.add({severity: 'error', summary: 'Nicht gefunden', detail: this.detailOf(error, 'Die angeforderte Ressource wurde nicht gefunden.')});
              break;
            case 409:
                this.messageService.add({severity: 'error', summary: 'Konflikt', detail: this.detailOf(error, 'Die Aktion steht im Konflikt mit dem aktuellen Stand der Daten.')});
                break;
            case 422:
              // Unprocessable Entity – kann ähnlich wie Validierungsfehler behandelt werden
              this.messageService.add({severity: 'warn', summary: 'Einige Eingaben sind fehlerhaft', detail: this.detailOf(error, 'Bitte prüfen Sie die mit Fehlern gekennzeichneten Eingabefelder.')});
              if (error.error?.errors) {
                this.validationErrorService.set(error.error.errors);
              }
              break;
            case 500:
            default:
              // Serverfehler zeigen bewusst keine Meldung aus dem Antwortkörper, sondern nur
              // eine generische Beschreibung samt traceId zum Nachschlagen im Server-Log.
              //
              // Anders als bei 4xx wird hier ausschließlich ProblemDetails ausgewertet und kein
              // blanker String übernommen: Fehler dieser Klasse stammen oft gar nicht von der
              // API, sondern vom Reverse Proxy — eine 502-HTML-Seite landete sonst vollständig
              // im Toast.
              const problem = this.problemDetailsOf(error);
              this.messageService.add({
                severity: 'error',
                summary: 'Serverfehler',
                detail: (problem?.detail || problem?.title || 'Es ist ein unerwarteter Fehler aufgetreten.')
                        + (problem?.traceId ? ` (Referenz: ${problem.traceId})` : '')
              });
              break;
          }
        }
        // Fehler weiterwerfen, damit Aufrufer ihn ggf. sehen (oder von GlobalErrorHandler erfasst wird)
        return throwError(() => error);
      })
    );
  }

  /**
   * Liest die anzeigbare Fehlermeldung aus der Antwort.
   *
   * Die API antwortet seit der zentralen Ausnahmebehandlung durchgängig mit ProblemDetails
   * (RFC 7807), also `{ title, detail, status, traceId }`. Zuvor kamen an manchen Stellen
   * blanke Strings — die werden weiterhin akzeptiert, damit ältere oder nicht umgestellte
   * Antworten nicht als "[object Object]" im Toast landen.
   */
  private detailOf(error: HttpErrorResponse, fallback: string): string {
    const body = error?.error;
    if (typeof body === 'string' && body.trim()) return body;
    return this.problemDetailsOf(error)?.detail || this.problemDetailsOf(error)?.title || fallback;
  }

  /**
   * Gibt den Antwortkörper zurück, wenn er wie ProblemDetails aussieht — sonst `null`.
   *
   * Absichtlich strenger als `detailOf`: Antworten, die nicht von der API stammen (etwa eine
   * HTML-Fehlerseite des Reverse Proxy), sollen nirgends als Meldungstext durchgereicht werden.
   */
  private problemDetailsOf(error: HttpErrorResponse): { title?: string; detail?: string; traceId?: string } | null {
    const body = error?.error;
    if (!body || typeof body !== 'object') return null;
    if (typeof body.detail !== 'string' && typeof body.title !== 'string') return null;
    return body;
  }
}